import UserApiBase from "../base/UserApiBase";

import { type OkResponse, OkResponseSchema } from "@/schemas/api/ok";
import {
  type PaginatedRequest,
  type PaginatedResponse,
} from "@/schemas/api/pagination";
import { type AppUserSession } from "@/schemas/auth/session";
import {
  PaginatedUserNotificationSchema,
  type UserNotification,
} from "@/schemas/user/notification";

/**
 * Service class for handling user notifications.
 * Extends the UserApiBase class to provide methods for interacting with the notification API.
 */
class UserNotificationService extends UserApiBase {
  /**
   * Constructs a new instance of the UserNotificationService.
   *
   * @param session - The user session object containing authentication details.
   */
  public constructor(session: AppUserSession) {
    super("/notification", session);
  }

  /**
   * Retrieves a paginated list of user notifications.
   *
   * @param page - The pagination request object containing page number and size.
   * @returns A promise that resolves to a paginated response of user notifications.
   */
  public async getNotifications(
    page: PaginatedRequest,
  ): Promise<PaginatedResponse<UserNotification>> {
    const paginateQueryStr = this.buildPaginateQueryStr(page);
    const response = await this.get(`/${paginateQueryStr}`);
    const validatedResponse = PaginatedUserNotificationSchema.parse(response);

    return validatedResponse;
  }

  /**
   * Marks a specific notification as read.
   *
   * @param notificationId - The ID of the notification to mark as read.
   * @returns A promise that resolves to an OkResponse indicating the operation was successful.
   */
  public async markNotificationAsRead(
    notificationId: string,
  ): Promise<OkResponse> {
    const response = await this.post(`/read/${notificationId}`, {});
    const validatedResponse = OkResponseSchema.parse(response);

    return validatedResponse;
  }

  /**
   * Marks all notifications as read.
   *
   * This method sends a POST request to the `/read_all` endpoint to mark all notifications as read.
   *
   * @returns A promise that resolves to an OkResponse indicating the operation was successful.
   */
  public async markAllNotificationsAsRead(): Promise<OkResponse> {
    const response = await this.post(`/read_all`, {});
    const validatedResponse = OkResponseSchema.parse(response);

    return validatedResponse;
  }
}

export default UserNotificationService;
